// Supabase Authentication Service for JWT-based auth with backend
import { getSupabaseClient } from './supabaseClient';
import { endpoint } from './endpoints';
import { getApiUrl } from './config';

export interface SupabaseUser {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  accountType: 'trial' | 'lite' | 'basic' | 'full_fledged' | 'enterprise' | 'super_agent';
  userType: 'agent' | 'admin' | 'agency_owner' | 'viewer';
  accountStatus?: 'pending' | 'pending_approval' | 'active' | 'approved' | 'suspended' | 'inactive';
  isActive: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  lastLoginAt?: string;
  isOnline?: boolean;
  mfaSettings?: {
    smsEnabled: boolean;
    totpEnabled: boolean;
    backupCodes: string[];
    trustedDevices: string[];
    preferredMethod: 'sms' | 'totp';
    sessionTimeoutMinutes: number;
    riskBasedAuth?: boolean;
  };
  trial?: {
    isTrialActive: boolean;
    trialStartDate: string;
    trialEndDate: string;
    trialExpiresAt: string;
  };
}

export interface SupabaseAuthResponse {
  user: SupabaseUser | null;
  session: any | null;
  nextStep?: 'mfa' | 'agency_select' | 'dashboard' | 'pending_approval';
  agencies?: any[];
  error?: string;
}

// LOGIN-HANG FIX (2026-07-24): the login path fetches our backend (/auth/profile,
// lockout status, failed-login tracking) with plain fetch() and NO timeout. A raw
// fetch never rejects on a stalled connection — if the backend accepts the socket
// but doesn't respond (peak-hours memory thrash / webhook stall → 502s), the await
// hangs FOREVER and the Sign In button spins with no error and no way forward.
// This wrapper aborts after `timeoutMs` so a stalled backend surfaces as a normal,
// retryable network error instead of an infinite spinner. Callers keep their own
// try/catch + retry — an abort looks to them exactly like any other fetch failure.
async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = 12000,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** Shown when the backend's IP blocklist answers 403 NETWORK_BLOCKED (VPN/proxy exits, banned IPs). */
export const NETWORK_BLOCKED_MESSAGE =
  "You can't sign in from this network. If you're using a VPN or proxy, turn it off and sign in from your own device and internet connection.";

/** The user closed the browser's passkey prompt (or it timed out), or the page replaced it with another one. */
export const passkeyCancelled = (error: unknown): boolean => {
  const e = error as any;
  return e?.code === 'ERROR_CEREMONY_ABORTED' || ['NotAllowedError', 'AbortError'].includes(e?.cause?.name ?? e?.name);
};

class SupabaseAuthService {
  // Deduplication: only one refresh/signOut runs at a time across all callers
  private _refreshPromise: Promise<any> | null = null;
  /** Why the last refreshSession() returned null. See refreshSession(). */
  _lastRefreshFailure: 'none' | 'rejected' | 'transport' = 'none';
  _lastSessionReadFailure: 'none' | 'rejected' | 'transport' = 'none';
  private _signOutPromise: Promise<boolean> | null = null;
  /**
   * Sign in with email and password using Supabase
   */
  async signInWithPassword(email: string, password: string, deviceId?: string, turnstileToken?: string): Promise<SupabaseAuthResponse> {
    try {
      // Get Supabase client
      const supabase = await getSupabaseClient();

      // CRITICAL FIX: DO NOT sign out before sign in!
      // The signOut() call was clearing deviceId from localStorage BEFORE login could read it,
      // breaking the "Remember Device" MFA feature. Supabase handles session cleanup automatically.
      // await supabase.auth.signOut(); // REMOVED - was clearing deviceId

      // Sign in with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { user: null, session: null, error: error.message };
      }

      if (!data.session) {
        return { user: null, session: null, error: 'No session created' };
      }

      return await this.finishSignIn(supabase, data.session, deviceId, turnstileToken);
    } catch (error) {
      return { user: null, session: null, error: 'Authentication failed' };
    }
  }

  /**
   * Sign in with a saved passkey (the browser's own prompt: Touch ID, Windows Hello, a phone).
   * `conditional` = passkey autofill: the browser offers the passkey from the email box instead of a
   * modal, and the promise waits until the user picks one. The backend skips the 2FA code for a
   * passkey sign-in only while every passkey on the account was added from Settings, so nextStep
   * can still come back "mfa".
   * `cancelled` = the user closed the prompt or the page started another one; show nothing.
   * `expired` = the challenge outlived its 5 minutes (autofill can wait longer); ask again.
   */
  async signInWithPasskey(
    deviceId?: string,
    opts: { conditional?: boolean; signal?: AbortSignal } = {},
  ): Promise<SupabaseAuthResponse & { cancelled?: boolean; expired?: boolean }> {
    try {
      const supabase = await getSupabaseClient();
      const { data, error } = await supabase.auth.signInWithPasskey({
        options: {
          ...(opts.conditional && { mediation: 'conditional' as const }),
          ...(opts.signal && { signal: opts.signal }),
        },
      });
      if (error) {
        const code = (error as any)?.code || (error as any)?.error_code;
        const expired = code === 'webauthn_challenge_expired';
        const message =
          code === 'passkey_disabled'
            ? "Passkey sign-in isn't available yet. Sign in with your password."
            : expired
              ? 'That took too long. Pick your passkey again.'
              : code === 'ERROR_INVALID_RP_ID' || code === 'ERROR_INVALID_DOMAIN'
                ? "Passkeys don't work on this web address. Sign in with your password."
                : "That passkey didn't work. Try again, or sign in with your password.";
        return { user: null, session: null, error: message, cancelled: passkeyCancelled(error), expired };
      }
      if (!data?.session) {
        return { user: null, session: null, error: 'No session created' };
      }
      return await this.finishSignIn(supabase, data.session, deviceId);
    } catch (error) {
      return { user: null, session: null, error: 'Authentication failed' };
    }
  }

  /**
   * Sign in with a one-time token another of our own servers handed over (Direct 130's "Open your CRM":
   * the backend's direct130-partner controller mints it with generateLink). The token works once and only
   * the newest one counts, so a stale tab gets the expired message.
   */
  async signInWithLinkToken(tokenHash: string, deviceId?: string): Promise<SupabaseAuthResponse> {
    try {
      const supabase = await getSupabaseClient();
      const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' });
      if (error || !data?.session) {
        return { user: null, session: null, error: 'That sign-in link has expired. Open the CRM again from Direct130.' };
      }
      return await this.finishSignIn(supabase, data.session, deviceId);
    } catch (error) {
      return { user: null, session: null, error: 'Authentication failed' };
    }
  }

  /** What every sign-in does once Supabase has a session: idle clock, backend profile, next step. */
  private async finishSignIn(
    supabase: any,
    session: any,
    deviceId?: string,
    turnstileToken?: string,
  ): Promise<SupabaseAuthResponse> {
    // A successful login IS activity: stamp the shared idle clock NOW so a
    // stale value left from a previous browsing session (crash, old tab,
    // any path that skipped signOut) can't make the auto-logout boot check
    // insta-kick this brand-new session as "idle".
    if (typeof window !== 'undefined') {
      try { localStorage.setItem('insuracentral_last_activity', Date.now().toString()); } catch { /* noop */ }
    }

    // Get user profile from backend using the Supabase JWT
    const profileResponse = await this.getUserProfile(session.access_token, deviceId, turnstileToken);

    if (profileResponse.error) {
      // If 409 CONFLICT, user is already logged in elsewhere - sign them out and return the error
      if (profileResponse.statusCode === 409) {
        // Sign out from Supabase since this login attempt should be blocked
        await supabase.auth.signOut();
      } else if (profileResponse.error === NETWORK_BLOCKED_MESSAGE) {
        // LOCAL only: a global signOut from a banned network would also end the owner's real sessions.
        await supabase.auth.signOut({ scope: 'local' });
      }
      return { user: null, session: null, error: profileResponse.error };
    }

    return {
      user: profileResponse.user,
      session,
      nextStep: profileResponse.nextStep,
      agencies: profileResponse.agencies,
    };
  }

  /**
   * Get user profile from backend using Supabase JWT
   */
  async getUserProfile(accessToken: string, deviceId?: string, turnstileToken?: string): Promise<{
    user: SupabaseUser | null;
    nextStep?: 'mfa' | 'agency_select' | 'dashboard';
    agencies?: any[];
    error?: string;
    statusCode?: number; // Added to return HTTP status code for special handling
  }> {
    const maxRetries = 3; // Reduced from 5

    // MASS-LOGOUT FIX (2026-07-22): ALWAYS send the device id. App-boot /
    // session-restore callers don't pass one, and a profile request without
    // X-Device-Id made the backend register a random "new device" for the same
    // Supabase session — evicting this browser's own session ~10s after login
    // ("logged out: signed in on another device"). The stable id lives in
    // localStorage (insuracentral_device_id, persisted across logouts).
    if (!deviceId && typeof window !== 'undefined') {
      try {
        deviceId = localStorage.getItem('insuracentral_device_id') || undefined;
      } catch { /* header simply omitted — backend now falls back deterministically */ }
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const apiUrl = getApiUrl('auth/profile');

        const response = await fetchWithTimeout(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            ...(deviceId && { 'X-Device-Id': deviceId }),
            ...(turnstileToken && { 'X-Turnstile-Token': turnstileToken }),
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (errorData.error === 'NETWORK_BLOCKED') {
            return { user: null, error: NETWORK_BLOCKED_MESSAGE, statusCode: response.status };
          }
          const errorMessage = errorData.message || `HTTP ${response.status}`;

          // Only retry on server errors (5xx) or network issues
          if (response.status >= 500 || response.status === 0) {
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, attempt * 1000));
              continue;
            }
          }

          // For client errors (4xx), don't retry
          // Include status code for special handling (e.g., 409 CONFLICT for duplicate sessions)
          return { user: null, error: errorMessage, statusCode: response.status };
        }

        const data = await response.json();

        return {
          user: data.user || data.data?.user,
          nextStep: data.nextStep || data.data?.nextStep,
          agencies: data.agencies || data.data?.agencies,
        };
      } catch (error) {
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, attempt * 1000));
        }
      }
    }

    return { user: null, error: 'Unable to fetch user profile. Please try again.' };
  }

  /**
   * Get current session from Supabase
   * Falls back to reading directly from localStorage if the Supabase client
   * has no session (happens in multi-device login scenarios where Supabase's
   * auto-refresh validation rejects the token on the server side, even though
   * our backend still accepts it).
   */
  async getCurrentSession() {
    this._lastSessionReadFailure = 'none';
    try {
      const supabase = await getSupabaseClient();
      const { data: { session }, error } = await supabase.auth.getSession();

      if (!error && session) {
        return session;
      }

      if (error) {
        // getSession() reads localStorage, but refreshes internally when the
        // stored token is expired — so it CAN fail on transport. A 4xx is the
        // server rejecting the token; anything else means we never got an
        // answer. See lastSessionReadWasTransportFailure().
        const status = (error as { status?: number }).status ?? 0;
        this._lastSessionReadFailure =
          status >= 400 && status < 500 ? 'rejected' : 'transport';
      }

      // Fallback: read session directly from localStorage
      // forceWriteSessionToStorage() writes here, and the Supabase client
      // may have cleared its in-memory state after failing server validation
      if (typeof window !== 'undefined') {
        const fallbackSession = this.readSessionFromStorage();
        if (fallbackSession?.access_token) {
          // Verify the token isn't expired before returning it
          if (fallbackSession.expires_at) {
            const now = Math.floor(Date.now() / 1000);
            if (now < fallbackSession.expires_at) {
              return fallbackSession;
            }
          } else {
            // No expiry info, return it anyway (let backend validate)
            return fallbackSession;
          }
        }
      }

      return null;
    } catch (error) {
      // Thrown here is always transport: failed fetch, DNS, abort, timeout.
      this._lastSessionReadFailure = 'transport';
      return null;
    }
  }

  /**
   * True when the last getCurrentSession() returned null because the auth server
   * was UNREACHABLE, rather than because the session is genuinely gone.
   *
   * Callers that would sign the user out on a null session MUST check this
   * first. Sibling of lastRefreshWasTransportFailure(); same 2026-08-28 outage.
   *
   * Note this only matters when the stored token is already expired — while it
   * is still valid the localStorage fallback above returns it and no caller ever
   * sees a null. That narrow window is exactly when a refresh is due, which is
   * also when a transport failure is most likely to be misread as a dead session.
   */
  lastSessionReadWasTransportFailure(): boolean {
    return this._lastSessionReadFailure === 'transport';
  }

  /**
   * Read session directly from localStorage, bypassing the Supabase client.
   * Returns the session object in the same format Supabase uses.
   */
  private readSessionFromStorage(): any | null {
    if (typeof window === 'undefined') return null;
    try {
      // First try the Supabase storage key
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const data = JSON.parse(raw);
            // Handle both direct session format and wrapped format
            if (data?.access_token) {
              return data;
            }
            if (data?.currentSession?.access_token) {
              return data.currentSession;
            }
            if (data?.session?.access_token) {
              return data.session;
            }
          }
        }
      }

      // Fallback: check our backup key (Supabase can't clear this one)
      const backupRaw = localStorage.getItem('insuracentral_session_backup');
      if (backupRaw) {
        const backup = JSON.parse(backupRaw);
        if (backup?.access_token) {
          return backup;
        }
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Get current user from Supabase
   */
  async getCurrentUser() {
    try {
      const supabase = await getSupabaseClient();
      const { data: { user }, error } = await supabase.auth.getUser();

      if (error) {
        return null;
      }

      return user;
    } catch (error) {
      return null;
    }
  }

  /**
   * Sign out from Supabase (deduplicated — concurrent callers share the same Promise)
   * IMPORTANT: Preserves deviceId for "Remember Device" MFA feature
   */
  async signOut(options?: { scope?: 'global' | 'local' | 'others' }) {
    // If a signOut is already in flight, wait on it instead of firing another
    // (first caller's scope wins for the shared promise)
    if (this._signOutPromise) {
      return this._signOutPromise;
    }

    this._signOutPromise = (async () => {
      try {
        // Save deviceId before signOut clears it
        const savedDeviceId = typeof window !== 'undefined'
          ? localStorage.getItem('insuracentral_device_id')
          : null;

        const supabase = await getSupabaseClient();
        // Default (no scope) = supabase-js default 'global' (revokes all devices) —
        // unchanged for manual logout. Auto-logout passes scope:'local' so an idle
        // device only signs out itself.
        const { error } = await supabase.auth.signOut(
          options?.scope ? { scope: options.scope } : undefined,
        );

        // Clear our session backup on logout
        if (typeof window !== 'undefined') {
          localStorage.removeItem('insuracentral_session_backup');
          // Also clear the shared idle clock. It survives tab close by design
          // (cross-tab activity), but left behind after a MANUAL logout it made
          // the auto-logout boot check read "idle for hours" on the NEXT visit
          // and instantly kick the fresh session to /auth/sign-in with the
          // "logged out due to inactivity" banner. A signed-out browser has no
          // idle clock to judge.
          localStorage.removeItem('insuracentral_last_activity');
        }

        // Restore deviceId after signOut - it must persist for "Remember Device"
        if (savedDeviceId && typeof window !== 'undefined') {
          localStorage.setItem('insuracentral_device_id', savedDeviceId);
        }

        if (error) {
          return false;
        }

        return true;
      } catch (error) {
        return false;
      }
    })();

    try {
      return await this._signOutPromise;
    } finally {
      this._signOutPromise = null;
    }
  }

  /**
   * Refresh session (deduplicated — concurrent callers share the same Promise)
   */
  async refreshSession() {
    // If a refresh is already in flight, wait on it instead of firing another
    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    this._refreshPromise = (async () => {
      // WHY THE FAILURE REASON IS RECORDED
      //
      // This returns null for two completely different situations, and callers
      // could not tell them apart:
      //
      //   REJECTED  — the refresh token is genuinely dead (expired, revoked,
      //               already consumed). The session really is over.
      //   TRANSPORT — we could not REACH the auth server at all: offline, DNS,
      //               timeout, 5xx, a pooler refusing connections. The session
      //               is probably still perfectly valid; we just could not ask.
      //
      // SessionValidator treated null as "session dead" and signed the user out.
      // So on 2026-08-28 a ~2 minute Supabase pooler outage (16:36-16:37 UTC,
      // :econnrefused) booted ~30 agents mid-shift, several of them on live
      // calls, and dropped them on a login screen that then told them their
      // password was wrong.
      //
      // A server we cannot reach is not a server saying "you are not allowed".
      this._lastRefreshFailure = 'none';
      try {
        const supabase = await getSupabaseClient();
        const { data, error } = await supabase.auth.refreshSession();

        if (error) {
          // Refresh token may have been consumed by another tab.
          // Check if Supabase client already has a valid session from localStorage
          // (another tab's successful refresh writes to shared storage).
          const { data: fallback } = await supabase.auth.getSession();
          if (fallback.session && fallback.session.expires_at && fallback.session.expires_at * 1000 > Date.now()) {
            return fallback.session;
          }
          // GoTrue sets status on a real rejection (400/401/403). No status, or a
          // 5xx, means we never got a verdict — treat that as transport.
          const status = (error as { status?: number }).status ?? 0;
          this._lastRefreshFailure =
            status >= 400 && status < 500 ? 'rejected' : 'transport';
          return null;
        }

        return data.session;
      } catch (error) {
        // A thrown error here is always transport — fetch failed, DNS, abort,
        // timeout. GoTrue reports a rejected token via `error`, not by throwing.
        this._lastRefreshFailure = 'transport';
        return null;
      }
    })();

    try {
      return await this._refreshPromise;
    } finally {
      this._refreshPromise = null;
    }
  }

  /**
   * True when the last refreshSession() failed because the auth server was
   * UNREACHABLE rather than because the token was rejected.
   *
   * Callers that would otherwise destroy the session must check this first — a
   * transient outage should retry, never sign the user out.
   */
  lastRefreshWasTransportFailure(): boolean {
    return this._lastRefreshFailure === 'transport';
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await this.getCurrentSession();
      return !!session;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get access token for API calls
   * Falls back to pending 2FA storage when Supabase client has no session
   * (happens in multi-device login scenarios where setSession() fails)
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const session = await this.getCurrentSession();
      if (session?.access_token) {
        return session.access_token;
      }

      // Fallback: check pending 2FA storage for a valid Supabase token
      // This handles the multi-device scenario where verify2FACode's setSession() failed
      // but we still have the original valid token from signInWithPassword()
      if (typeof window !== 'undefined') {
        try {
          const pendingRaw = sessionStorage.getItem('insuracentral_pending_2fa');
          if (pendingRaw) {
            const pending = JSON.parse(pendingRaw);
            if (pending?.session?.access_token) {
              return pending.session.access_token;
            }
          }
        } catch {
          // Silent fail
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Clear all tokens (simplified - removes only necessary tokens)
   * IMPORTANT: Preserves deviceId for "Remember Device" MFA feature
   */
  async clearAllTokens(): Promise<void> {
    try {
      // Save deviceId before signOut clears it
      const savedDeviceId = typeof window !== 'undefined'
        ? localStorage.getItem('insuracentral_device_id')
        : null;

      const supabase = await getSupabaseClient();
      await supabase.auth.signOut();

      // Clear our session backup
      if (typeof window !== 'undefined') {
        localStorage.removeItem('insuracentral_session_backup');
      }

      // Restore deviceId after signOut - it must persist for "Remember Device"
      if (savedDeviceId && typeof window !== 'undefined') {
        localStorage.setItem('insuracentral_device_id', savedDeviceId);
      }
    } catch (error) {
      // Silently handle token clearing errors
    }
  }

  /**
   * Track failed login attempt - call this when Supabase auth fails
   * This tracks failed attempts on the backend for rate limiting
   */
  async trackFailedLogin(email: string): Promise<{
    locked: boolean;
    remainingAttempts?: number;
    lockoutMinutes?: number;
    backendError?: boolean;
  }> {
    try {
      const apiUrl = getApiUrl('auth/track-failed-login');

      const response = await fetchWithTimeout(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      }, 8000);

      if (!response.ok) {
        // Backend call failed - frontend should handle local tracking
        console.warn('Failed to track login attempt on backend:', response.status);
        return { locked: false, backendError: true };
      }

      const json = await response.json();
      // Handle wrapped response from backend (data property)
      const result = json.data || json;
      return result;
    } catch (error) {
      // Backend call failed - frontend should handle local tracking
      console.warn('Error tracking login attempt:', error);
      return { locked: false, backendError: true };
    }
  }

  /**
   * Force-write Supabase session to localStorage, bypassing network validation.
   *
   * setSession() always calls Supabase's server (_getUser or _callRefreshToken)
   * to validate the token. In multi-device login scenarios, the old session was
   * invalidated on Supabase's auth server, so setSession() fails even though
   * our backend still accepts the JWT.
   *
   * This method writes the session directly to localStorage in the format
   * Supabase expects, so after a page reload the Supabase client can read it.
   */
  forceWriteSessionToStorage(accessToken: string, refreshToken: string): boolean {
    if (typeof window === 'undefined') return false;
    try {
      // Decode the JWT payload to extract user info and expiry
      const parts = accessToken.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

      const expiresAt = payload.exp || (Math.floor(Date.now() / 1000) + 3600);
      const expiresIn = expiresAt - Math.floor(Date.now() / 1000);

      // Build session object in Supabase's expected format
      const sessionData = {
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: 'bearer',
        expires_in: expiresIn,
        expires_at: expiresAt,
        user: {
          id: payload.sub || '',
          email: payload.email || '',
          aud: payload.aud || 'authenticated',
          role: payload.role || 'authenticated',
          user_metadata: payload.user_metadata || {},
          app_metadata: payload.app_metadata || {},
        },
      };

      // Find the Supabase storage key by scanning localStorage
      // Supabase uses the pattern: sb-{project_ref}-auth-token
      let storageKey = '';
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
          storageKey = key;
          break;
        }
      }

      // If no existing key found, derive it from the Supabase URL
      if (!storageKey) {
        // Try direct URL first, then deobfuscated
        let supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
        if (!supabaseUrl) {
          // Deobfuscate the same way supabaseClient.ts does
          const obfuscated = process.env.NEXT_PUBLIC_SB_U || '';
          if (obfuscated) {
            try {
              const clean = obfuscated.replace(/^["']|["']$/g, '');
              const reversed = clean.split('').reverse().join('');
              supabaseUrl = atob(reversed);
            } catch { /* ignore */ }
          }
        }
        if (supabaseUrl) {
          try {
            const hostname = new URL(supabaseUrl).hostname;
            const prefix = hostname.split('.')[0];
            storageKey = `sb-${prefix}-auth-token`;
          } catch { /* ignore */ }
        }
      }

      // Last resort fallback
      if (!storageKey) {
        storageKey = 'supabase.auth.token';
      }

      localStorage.setItem(storageKey, JSON.stringify(sessionData));

      // Also store a backup copy under our own key that Supabase won't touch.
      // Supabase's autoRefreshToken runs _recoverAndRefresh() on client init,
      // which can clear the sb-* key if server validation fails.
      // This backup ensures readSessionFromStorage() can still find the session.
      localStorage.setItem('insuracentral_session_backup', JSON.stringify(sessionData));

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check lockout status without incrementing failed attempts
   * Call this on page load to restore lockout state after refresh
   */
  async checkLockoutStatus(email: string): Promise<{
    locked: boolean;
    remainingAttempts?: number;
    lockoutMinutes?: number;
  }> {
    if (!email) {
      return { locked: false, remainingAttempts: 5 };
    }

    try {
      const apiUrl = getApiUrl('auth/check-lockout-status');

      const response = await fetchWithTimeout(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      }, 8000);

      if (!response.ok) {
        console.warn('Failed to check lockout status:', response.status);
        return { locked: false, remainingAttempts: 5 };
      }

      const json = await response.json();
      // Handle wrapped response from backend (data property)
      const result = json.data || json;
      return result;
    } catch (error) {
      console.warn('Error checking lockout status:', error);
      return { locked: false, remainingAttempts: 5 };
    }
  }
}

export default new SupabaseAuthService();
